// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { setChargeBillableState } from '@/billing/api/set-charge-billable-state';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';

// Covers both mark-billable and mark-not-billable paths since the same
// mutation handles both directions.
const EXPECTED_ERROR_CODES = new Set<number>([
  ERROR_CODE_MAPPING.charge_not_markable_billable,
  ERROR_CODE_MAPPING.charge_not_markable_not_billable,
]);

interface UseSetChargeBillableStateOptions {
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

interface SetChargeBillableStateVariables {
  chargeKey: string;
  markBillable: boolean;
  // Only read on the mark-not-billable path. Supplying a value overwrites the
  // charge's expected_billable_date on the server and emits an audit delta
  // alongside the status flip.
  expectedBillableDate?: DateTime;
}

export function useSetChargeBillableState(
  options?: UseSetChargeBillableStateOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: SetChargeBillableStateVariables) =>
      setChargeBillableState(
        variables.chargeKey,
        variables.markBillable,
        variables.markBillable
          ? undefined
          : { expectedBillableDate: variables.expectedBillableDate }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['charges'] });
      options?.onSuccess?.();
    },
    onError: async (error: AxiosError) => {
      const apiError = handleAPIError(error);
      const expected = EXPECTED_ERROR_CODES.has(apiError.apiStatusCode);
      if (!apiError.sentToSentry && !expected) {
        Sentry.captureException(error);
      }
      if (expected) {
        await queryClient.invalidateQueries({ queryKey: ['charges'] });
      }
      options?.onError?.(
        getUserFacingErrorMessage(apiError),
        apiError.apiStatusCode,
        apiError.httpStatusCode
      );
    },
  });
}
